"use client";
import React, { useMemo, useRef, useState } from "react";
import { useEffect } from "react";
import { generators } from "../shared/generators/generators";
import { useGlobalContext } from "../../app/context/store";
import { getLocalizedPath } from "../shared/formatURL";
import { ChevronLeft, ChevronRight, ChevronUp, Expand, Gem, Move } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import useSWRInfinite from "swr/infinite";
import Image from "next/image";
import { useDisplayModeContext } from "@/app/context/displayModeStore";
import TagCardWrapper from "./tagCard/tagCardWrapper";
import useUgc from "@/app/context/UgcContext";
import { CategoryTextUgc } from "./CategoryTextUgc";
import cookie from "cookie";
import { findStyleNameByIdAndLocale } from "@/components/shared/generators/generators";
import { useSignInModal } from "./sign-in-modal";
import ActionTagCardWrapper from "./tagCard/actionTagCardWrapper";
import ViewToggle from "./toggles/viewToggle";
import ListView from "./listView";
import { fetcher } from "@/lib/api/fetcher";
import { useGenerator } from "@/app/context/GeneratorContext";

export default function UgcTagsPage({ translations }: { translations: any }) {
  const { user } = useGlobalContext();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { state, dispatch } = useDisplayModeContext();
  const [sliderValue, setSliderValue] = useState(1);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(6);
  const [fullScreen, setFullScreen] = useState(false);
  const infiniteScrollBoundaryRef = useRef<HTMLDivElement | null>(null);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [isLoadingAllTags, setIsLoadingAllTags] = useState(false);
  const { generatorList, generatorListSuccess } = useGenerator();

  if (!generatorListSuccess) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Image
          src={"https://cdn6.imgpog.com/loadingGIF.webp"}
          width={50}
          height={50}
          alt="Loading"
          unoptimized
        />
      </div>
    );
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSliderChange = (event: any) => {
    setSliderValue(event.target.value);
    // Serialize the cookie string using the `cookie` library
    const serializedCookie = cookie.serialize("sliderValue", event.target.value, {
      path: "/",
      maxAge: 365 * 24 * 60 * 60 * 999, // Max age in seconds
    });
    // Set the cookie in the browser
    document.cookie = serializedCookie;
    switch (event.target.value) {
      case "0":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 3, lg: 2, md: 2, sm: 1 },
        });
        break;
      case "1":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 4, lg: 3, md: 2, sm: 2 },
        });
        break;
      case "2":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 5, lg: 4, md: 3, sm: 3 },
        });
        break;
      case "3":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 6, lg: 5, md: 4, sm: 4 },
        });
        break;
      case "4":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 7, lg: 6, md: 5, sm: 5 },
        });
        break;
      case "5":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 8, lg: 7, md: 6, sm: 5 },
        });
        break;
      case "6":
        dispatch({
          type: "SET_VIEWING_MODE",
          payload: { default: 9, lg: 8, md: 7, sm: 6 },
        });
        break;
      default:
        // Handle unexpected values
        console.error("Unexpected slider value");
    }
  };

  const updateRangeValues = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 768) {
      // Example breakpoint for mobile
      setMinValue(0);
      setMaxValue(4);
    } else {
      setMinValue(0);
      setMaxValue(6);
    }
  };

  function toggleFullScreen() {
    setFullScreen(!fullScreen);
    if (!document.fullscreenElement) {
      // Enter full screen
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      // Exit full screen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  function toggleExpandContent() {
    // Serialize the cookie string using the `cookie` library
    const serializedCookie = cookie.serialize("expandContent", String(!state.expandContent), {
      path: "/",
      maxAge: 365 * 24 * 60 * 60 * 999, // Max age in seconds
    });
    // Set the cookie in the browser
    document.cookie = serializedCookie;
    dispatch({ type: "SET_EXPAND_CONTENT", payload: "" });
  }

  const { currMaster, currStyle, selectedStyleGroupsUgc, fetchStyleUgc } = useUgc();

  const searchParams = useSearchParams();
  const isBrowsing = searchParams.get("browse") === "true";
  const listView = searchParams.get("view") === "list";
  const defaultStyle = generatorList[0].shortName;

  const [currTab, setCurrTab] = useState(searchParams.get("filter") || "hot");

  const constructUrl = (style: string, groups: string, tab: string): string => {
    const baseUrl = `/tag/collection?sort=${tab}&style=${style}`;

    // Include groups parameter only if it's not ""
    const groupsParam = groups !== "" && groups !== "all" ? `&groups=${groups}` : "";

    // Construct the full URL
    return `${baseUrl}${groupsParam}`;
  };

  const constructListUrl = (style: string, groups: string, locale: string): string => {
    const baseUrl = `/tag/list?style=${style}&locale=${locale}`;
    const groupsParam = groups !== "" && groups !== "all" ? `&groups=${groups}` : "";
    return `${baseUrl}${groupsParam}`;
  };

  const getKey = useMemo(() => {
    return (pageIndex: number, previousPageData: any) => {
      // If the first page, construct the URL using currStyle, currGroups, and currTab
      if (pageIndex === 0) {
        return constructUrl(currStyle, selectedStyleGroupsUgc.join(","), currTab);
      }

      // If the previous page data has fewer items than expected, stop fetching
      if (previousPageData && previousPageData?.tags.length < 10) {
        return null;
      }

      // If there's no more data, stop fetching
      if (!previousPageData || !previousPageData.next) {
        return null;
      }

      // Otherwise, use the next URL provided by the previous page's data
      return previousPageData.next;
    };
  }, [currStyle, selectedStyleGroupsUgc, currTab, currMaster]);

  const { data, error, setSize, size } = useSWRInfinite(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateAll: false,
    revalidateFirstPage: false,
  });

  // Get all tags for list view
  const fetchAllTags = async (url: string): Promise<any[]> => {
    try {
      let allTags: any[] = [];
      let currentUrl = url;
      // Loops to keep getting tags until there are no more & stores in array
      while (currentUrl) {
        const response = await fetcher(currentUrl);
        const tags = response.tags || [];
        allTags = [...allTags, ...tags];
        currentUrl = response.next;
        if (tags.length === 0) {
          break;
        }
      }

      return allTags;
    } catch (error) {
      console.error("Error fetching tags:", error);
      return [];
    }
  };

  // Set loading state and update all tags with fetched tags
  useEffect(() => {
    const loadAllTags = async () => {
      setIsLoadingAllTags(true);
      try {
        const initialUrl = constructListUrl(
          currStyle,
          selectedStyleGroupsUgc[0] || "",
          translations.key,
        );
        const allTagsData = await fetchAllTags(initialUrl);
        setAllTags(allTagsData);
      } catch (error) {
        console.error("Error loading all tags:", error);
      } finally {
        setIsLoadingAllTags(false);
      }
    };

    if (currStyle) {
      loadAllTags();
    }
  }, [currStyle, selectedStyleGroupsUgc, translations.key]);

  const tags = data // Flatten the nested array to a single array
    ? ([] as any[]).concat(...data.map((page) => page.tags))
    : [];

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (data && typeof data[size - 1] === "undefined");
  const isReachingEnd =
    data && (data[data.length - 1]?.next === null || data[data.length - 1]?.tags.length === 0);

  useEffect(() => {
    updateRangeValues();

    const parsedCookies = cookie.parse(document.cookie);

    if (parsedCookies.sliderValue) {
      handleSliderChange({ target: { value: parsedCookies.sliderValue } });
    }

    if (parsedCookies.expandContent) {
      if (parsedCookies.expandContent === "true") {
        dispatch({ type: "SET_EXPAND_CONTENT", payload: true });
      }
    }

    window.addEventListener("resize", updateRangeValues);

    const fetchData = async (styleId: string, groupArray: string[], name: string) => {
      try {
        await fetchStyleUgc(name, groupArray, styleId);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const styleParamGenerator =
      generatorList.find((generator) => generator.shortName === searchParams.get("style")) ||
      generatorList[0];

    if (!currStyle) {
      fetchData(generatorList[0]._id, selectedStyleGroupsUgc, defaultStyle);
    } else if (searchParams.get("style") !== currStyle) {
      fetchData(
        styleParamGenerator._id || generatorList[0]._id,
        selectedStyleGroupsUgc,
        searchParams.get("style") || defaultStyle,
      );
    } else {
      const currGenerator =
        generatorList.find((generator) => generator.shortName === currStyle) || generatorList[0];
      fetchData(currGenerator._id, selectedStyleGroupsUgc, currStyle);
    }

    // Inline function for scroll event handling
    const handleScroll = () => {
      const shouldShow = window.scrollY > 200;
      setShowScrollTop(shouldShow);
    };
    // Setup the scroll listener
    window.addEventListener("scroll", handleScroll);
    // Inline function for intersection handling and observer setup
    let observer: IntersectionObserver | null = null;
    if (!isLoadingMore && !error && infiniteScrollBoundaryRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setSize(size + 1);
          }
        },
        { threshold: 1 },
      );
      observer.observe(infiniteScrollBoundaryRef.current);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateRangeValues);
      observer?.disconnect();
    };
  }, [
    currStyle,
    isLoadingMore,
    error,
    infiniteScrollBoundaryRef,
    setSize,
    searchParams.get("style"),
  ]);

  const newURL = getLocalizedPath(`tags/create`);

  return (
    <div className="relative">
      <div className={`mx-auto px-5 xl:max-w-[88rem]`}>
        <div className="flex flex-row">
          {/* <div className="text-lg font-bold text-white sm:text-xl">
          All "sortBy" Categories for "XXX" Group(s) for X Style
          </div> */}
          <CategoryTextUgc
            sortBy={currTab}
            queryParam={null}
            pathCode={translations.key}
            style={currStyle}
            selectedGroups={selectedStyleGroupsUgc}
            translations={translations}
            isListView={listView}
          />
          {/* Flex container with justify-center */}
          {/* todo: center button below */}
          {isBrowsing && (
            <Link
              href={newURL}
              prefetch={false}
              className="ml-auto flex flex-row hover:brightness-105"
            >
              <div className="sm:text-md mb-auto ml-2 mt-1 whitespace-nowrap text-sm font-bold text-[#6CB4F4]">
                + {translations.createTag}
              </div>
            </Link>
          )}
        </div>
        {/* <div className="mt-2">
          <StyleToggleSet locale={locale} />
        </div> */}
        <div>
          <GroupToggleSet
            currTab={currTab}
            setCurrTab={setCurrTab}
            translations={translations}
            isBrowsing={isBrowsing}
          />
        </div>
      </div>

      {!listView && (
        <div className="mx-auto mb-3.5 mt-2 flex flex-row items-center justify-center px-5 xl:max-w-[88rem]">
          <div className="flex-grow">
            <input
              type="range"
              aria-label="Slider"
              min={minValue}
              max={maxValue}
              value={sliderValue}
              onChange={handleSliderChange}
              className="h-2 w-full cursor-pointer appearance-none rounded-lg border-[0.5px] border-[#3C3C3C] bg-[#2B2D31]"
            />
          </div>
          <button
            onClick={toggleExpandContent}
            className={`${
              false ? "" : ""
            } ml-4 hidden flex-row overflow-hidden whitespace-nowrap rounded-md py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:flex`}
          >
            <Move color="white" className="mx-1 my-auto h-3.5 w-auto" />
            {state.expandContent ? translations.shrinkContent : translations.expandContent}
          </button>
          <button
            onClick={toggleFullScreen}
            className={`${
              false ? "" : ""
            } ml-4 hidden flex-row rounded-md py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:flex`}
          >
            <Expand color="white" className="mx-1 my-auto h-3.5 w-auto" />
            {fullScreen ? translations.exitFullScreen : translations.fullScreen}
          </button>
        </div>
      )}

      <div
        className={`mx-auto mb-[100vh] px-0 ${
          state.expandContent ? "" : "sm:px-[7px] xl:max-w-[88rem]"
        }`}
      >
        {!listView ? (
          isBrowsing ? (
            <ActionTagCardWrapper data={tags} style={currStyle} translations={translations} />
          ) : (
            <TagCardWrapper data={tags} style={currStyle} translations={translations} />
          )
        ) : (
          <ListView
            tagData={allTags}
            currStyle={currStyle}
            translations={translations}
            isLoading={isLoadingAllTags}
          />
        )}

        {
          <>
            <div ref={infiniteScrollBoundaryRef} className="-mt-[55vh]" />
            {/* Loading Icon */}
            {isLoadingMore && !isReachingEnd && !error && tags.length % 10 === 0 && (
              <div className="flex">
                <Image
                  src={"https://cdn6.imgpog.com/loadingGIF.webp"}
                  width={50}
                  height={50}
                  alt="Loading"
                  className="mx-auto mb-[55vh] mt-[55vh]"
                  unoptimized
                />
              </div>
            )}
          </>
        }
      </div>
      <div className="fixed bottom-0 flex h-[6.25rem] w-full bg-gradient-to-t from-[#131315] via-[#131315]/70 to-transparent sm:bottom-16 sm:h-0 sm:max-w-[80rem] sm:bg-none">
        <button
          className={`${
            user.membership && user?.membership.level === 1 ? "hidden" : "block"
          } flex w-auto pl-10 sm:hidden`}
          aria-label="Upgrade"
        >
          <Link
            prefetch={false}
            href={getLocalizedPath("upgrade") + "?ref=scroll"}
            aria-label="Upgrade"
            className="mb-auto mr-auto flex animate-fade-up-sped rounded-full bg-[#FFCC18] px-3 py-2 text-sm font-bold text-white shadow-2xl hover:brightness-105"
          >
            <Gem color="black" className="mx-0.5 my-auto h-5 w-auto px-[0.85px] py-0.5" />
          </Link>
        </button>
        {showScrollTop && (
          <div className={`z-1000 ml-auto pr-10`}>
            <button
              onClick={scrollToTop}
              className="flex animate-fade-up-sped rounded-full bg-[#DD0772] px-3 py-2 text-sm font-bold text-white shadow-2xl hover:brightness-105 sm:mt-[-2.25rem]"
            >
              <ChevronUp color="white" className="my-auto h-5 w-auto" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface TabLinkProps {
  label: string; // e.g., 'top', 'new', 'all'
  current: string; // current tab or generator
  setCurrent: any; // function to set the current tab or generator
  isTab?: boolean; // differentiate between tab and generator
}

const GroupToggleSet = ({
  translations,
  currTab,
  setCurrTab,
  isBrowsing,
}: {
  translations: any;
  currTab: string;
  setCurrTab: any;
  isBrowsing: boolean;
}) => {
  // const { state, dispatch } = useGeneratorContext();
  const { selectedStyleUgc } = useUgc();
  // Ref for the scrollable container
  const searchParams = useSearchParams();
  const browseQueryParam = isBrowsing ? "true" : "";
  const styleQueryParam = searchParams.get("style");
  const groupQueryParam = searchParams.get("group");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listViewQueryParam = searchParams.get("view");

  // Function to scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -500, behavior: "smooth" });
    }
  };

  // Function to scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 500, behavior: "smooth" });
    }
  };
  const TabLink: React.FC<TabLinkProps> = ({ label, current, setCurrent, isTab = true }) => {
    const query = {
      filter: isTab ? label : currTab,
      // style: isTab ? currGenerators : label,
      ...(groupQueryParam && { group: groupQueryParam }),
      ...(styleQueryParam && { style: styleQueryParam }),
      ...(browseQueryParam && { browse: browseQueryParam }),
    };

    return (
      <Link
        prefetch={false}
        href={{ pathname: ``, query }}
        onClick={() => setCurrent(label)}
        className={`${
          current === label
            ? "border-[0.5px] border-[#DD0772] bg-[#DD0772]"
            : "border-[0.5px] border-[#3C3C3C] bg-[#2B2D31]"
        } xs:ml-0 mr-2.5 whitespace-nowrap rounded-md px-4 py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300`}
      >
        {translations[label]}
      </Link>
    );
  };

  return (
    <div className={`z-10 mb-0 flex items-center`}>
      <button
        onClick={scrollLeft}
        aria-label="Left Arrow"
        className="scroll-arrow left-arrow z-10 hidden border-none bg-[#202124] px-2 py-1.5 focus:outline-none sm:flex"
      >
        <ChevronLeft className="text-white" />
      </button>
      <div
        ref={scrollContainerRef}
        className="xs:mx-0 mx-[-18px] flex items-center overflow-hidden overflow-x-scroll whitespace-nowrap scrollbar-hide sm:w-full"
      >
        <StyleToggle translations={translations} />
        <div className="mx-2.5 inline-block h-4 border-r border-white"></div>
        <ViewToggle translations={translations} />
        <div className="inline-block h-4 border-r border-white"></div>
        {listViewQueryParam !== "list" && (
          <>
            <div className="ml-2.5 flex">
              <TabLink label="hot" current={currTab} setCurrent={setCurrTab} />
              <TabLink label="new" current={currTab} setCurrent={setCurrTab} />
              <TabLink label="top" current={currTab} setCurrent={setCurrTab} />
            </div>
            <div className="inline-block h-4 border-r border-white"></div>
          </>
        )}

        {selectedStyleUgc.tagGroups.map((tagGroup: any, i: any) => (
          <GroupToggle key={i} order={i} tagGroup={tagGroup} translations={translations} />
        ))}
      </div>
      <button
        onClick={scrollRight}
        aria-label="Right Arrow"
        className="scroll-arrow right-arrow z-10 ml-5 hidden border-none bg-[#202124] px-2 py-1.5 focus:outline-none sm:flex"
      >
        <ChevronRight className="text-white" />
      </button>
    </div>
  );
};

const StyleToggle = ({ translations }: { translations: any }) => {
  const { generatorList } = useGenerator();
  const searchParams = useSearchParams();
  const defaultStyle = generatorList[0].shortName;

  const { SignInModal, setShowSignInModal } = useSignInModal({
    selectStyle: translations.selectStyle,
    index: translations.index,
  });

  return (
    <>
      <SignInModal />
      <button
        // onClick={handleToggle}
        onClick={() => setShowSignInModal(true)}
        type="button"
        className={`order-[0.5px] ml-5 whitespace-nowrap rounded-md border-[#DD0772] bg-[#DD0772] px-4 py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300`}
      >
        {translations.styleH1Singular}:{" "}
        {findStyleNameByIdAndLocale({
          id: searchParams.get("style") || defaultStyle,
          locale: translations.key,
        })}
      </button>
    </>
  );
};

const GroupToggle = ({
  tagGroup,
  order,
  translations,
}: {
  tagGroup: any;
  order: any;
  translations: any;
}) => {
  const { selectedStyleGroupsUgc, setSelectedStyleGroupsUgc } = useUgc();
  const [isSelected, setIsSelected] = useState(selectedStyleGroupsUgc.includes(tagGroup._id));

  const handleToggle = () => {
    let updatedGroups = [...selectedStyleGroupsUgc];

    if (tagGroup._id === "all") {
      updatedGroups = ["all"];
    } else {
      // Deselect "all" if another group is selected
      updatedGroups = updatedGroups.filter((id) => id !== "all");

      if (isSelected) {
        // If the group is already selected, remove it
        updatedGroups = updatedGroups.filter((id) => id !== tagGroup._id);
      } else {
        // If the group is not selected, add it
        updatedGroups.pop();
        updatedGroups.push(tagGroup._id);
      }

      // If no other groups are selected, select "all" by default
      if (updatedGroups.length === 0) {
        updatedGroups.push("all");
      }
    }

    // Sort groups alphabetically before updating state and URL
    updatedGroups.sort();

    // Update the state with the new array
    setSelectedStyleGroupsUgc(updatedGroups);

    // Update the URL while preserving other query parameters
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("group", updatedGroups[0]);
    let newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    newUrl = decodeURIComponent(newUrl);
    window.history.replaceState(null, "", newUrl);

    // Update the local selected state
    setIsSelected(!isSelected);
  };

  useEffect(() => {
    setIsSelected(selectedStyleGroupsUgc.includes(tagGroup._id));
  }, [selectedStyleGroupsUgc]);
  return (
    <button
      onClick={handleToggle}
      type="button"
      className={`${
        isSelected
          ? "border-[0.5px] border-[#DD0772] bg-[#DD0772]"
          : "border-[0.5px] border-[#3C3C3C] bg-[#2B2D31]"
      } ${
        order === 0 ? "ml-2.5" : "ml-0"
      } xs:ml-0 mr-2.5 whitespace-nowrap rounded-md px-4 py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300`}
    >
      {tagGroup._id === "all"
        ? tagGroup.names[Number(translations.index)].value
        : tagGroup.names[translations.key]}
    </button>
  );
};
